/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { eosApiService } from '../../../services/api/api.eos'
import TableMenu from './TableMenu'
import type { EosSiteRow, EosPaginatedResponse, EosTableQueryParams } from './types'
import { RcmTable } from '../../rcm-table/RcmTable'
import { RcmTableDataWrapper } from '../../rcm-table/RcmTableDataWrapper'
import { TableHeadRow, TableHeadCell, TableRow } from '../../table/TableComponents'
import { useRcmTableData } from '../../../hooks/useRcmTableData'

// ─── Constants ───────────────────────────────────────────────────────────

const PERSISTENCE_KEY = 'EOS_DASHBOARD_SITES_TABLE'
const DEFAULT_SORT_BY = 'STUDY_NAME'

// ─── Data Loader ─────────────────────────────────────────────────────────

async function loadSites(queryParams: EosTableQueryParams) {
  const resp = await eosApiService.getUserSites(queryParams)
  const data = resp as EosPaginatedResponse<EosSiteRow>
  return {
    elements: data.elements || [],
    totalElements: data.totalElements ?? (data.elements?.length || 0),
    hasNextPage: !!data.hasNextPage
  }
}

// ─── Inner Table (consumes RcmTableDataWrapper context) ──────────────────

const SitesTableInner: React.FC = () => {
  const { t } = useTranslation()
  const tableData = useRcmTableData()

  // Match legacy behavior: hide entire section when there are no rows and we're not loading.
  if (!tableData.isLoading && (!tableData.entries || tableData.entries.length === 0)) {
    return null
  }

  return (
    <div>
      <div>
        <h2>{t('EOS_DASHBOARD_SITES_TABLE_TITLE')}</h2>
      </div>
      <RcmTable
        className="chita-table"
        noResultsText="NO_RESULTS"
        rowKey="siteFolderId"
        HeadRow={
          <TableHeadRow>
            <TableHeadCell sort="STUDY_NAME">{t('TABLE_TH_STUDY_NAME')}</TableHeadCell>
            <TableHeadCell sort="SITE_NAME">{t('TABLE_TH_SITE_NAME')}</TableHeadCell>
            <TableHeadCell id="fd4ea078-7d1c-4550-be84-14ead0e2f602">{t('TASKS_TABLE_HEADER_ACTIONS')}</TableHeadCell>
          </TableHeadRow>
        }
        Row={(props: { row: EosSiteRow; index: number }) => {
          const { row } = props
          return (
            <TableRow {...props}>
              <td>{row.studyName}</td>
              <td>{row.siteName}</td>
              <td className="actions">
                <TableMenu item={row} itemKey={row.siteFolderId} />
              </td>
            </TableRow>
          )
        }}
      />
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────

const SitesTable: React.FC = () => {
  return (
    <RcmTableDataWrapper
      doPreload={true}
      loadData={loadSites}
      defaultSortBy={DEFAULT_SORT_BY}
      defaultSortDesc={true}
      persistenceKey={PERSISTENCE_KEY}
    >
      <SitesTableInner />
    </RcmTableDataWrapper>
  )
}

export default SitesTable
